import { canUseDOM } from '@ps/web-core-utils';
import React, { useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import { GooglePayNativePaymentError } from '../../../../../../types/Cashier';
import { DepositStatus } from '../../../../../../types/status';
import { getGooglePayNativeData } from '../../../../../../utils/googlePay';
import CTAButton from '../../../../../CTAButton/CTAButton';

import { TXTWCL_Deposit } from './GooglePay.messages.json';

interface GooglePayResult {
  failed?: boolean;
  cancelled?: boolean;
  timeout?: boolean;
  paymentToken?: string;
  cardInfo?: string;
}

const paymentTokenTimeoutSpan = 500000; // in ms = 500 secs

const GooglePay: React.FC = () => {
  const starsWebGooglePay = canUseDOM && window.starsweb?.googlePay;
  const site = useSelector(select.cms.getSiteId);
  const form = useForm();
  const [processing, setProcessing] = useState(false);

  const { data } = useWizardContext();

  const {
    input: {
      value: { amount, currency }
    }
  } = useField('deposit', { subscription: { value: true } });

  const {
    paySystemDetails: { info }
  } = data;

  const waitForEventWithTimeout = (): Promise<GooglePayResult> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        resolve({ timeout: true });
      }, paymentTokenTimeoutSpan);

      const listener = (e: CustomEvent) => {
        const payload = e.detail && e.detail.payload;
        const cancelled: boolean =
          payload && payload.result === GooglePayNativePaymentError.Cancelled;
        const failed: boolean = payload && payload.result === GooglePayNativePaymentError.Failed;
        const paymentMethodData = payload.paymentMethodData || {};
        const cardNetwork =
          (paymentMethodData && paymentMethodData.info && paymentMethodData.info.cardNetwork) || '';
        const cardDetails =
          (paymentMethodData && paymentMethodData.info && paymentMethodData.info.cardDetails) || '';
        const cardInfo = [cardNetwork, cardDetails].join(' ');
        const data: GooglePayResult = {
          cancelled,
          timeout: false,
          failed,
          paymentToken:
            paymentMethodData &&
            paymentMethodData.tokenizationData &&
            paymentMethodData.tokenizationData.token,
          cardInfo: cardInfo.charAt(0).toUpperCase() + cardInfo.substr(1).toLowerCase()
        };

        clearTimeout(timer);
        // eslint-disable-next-line stars-web/no-direct-browser-globals-access
        document.removeEventListener('googlePayPaymentResponse', (e) => listener(e as CustomEvent));
        resolve(data);
      };
      // eslint-disable-next-line stars-web/no-direct-browser-globals-access
      document.addEventListener('googlePayPaymentResponse', (e) => listener(e as CustomEvent));
    });

  const handleResponse = (result: GooglePayResult) => {
    const { paymentToken, cancelled, timeout, failed, cardInfo } = result;
    if (paymentToken) {
      form.change('deposit.googlePayPaymentToken', paymentToken);
      if (cardInfo) {
        form.change('deposit.googlePayCardInfo', cardInfo);
      }
    } else if (timeout) {
      data.googlePayNativeResultError = { status: DepositStatus.GOOGLE_PAY_SESSION_TIMEOUT };
    } else if (cancelled) {
      data.googlePayNativeResultError = { status: DepositStatus.GOOGLE_PAY_CANCELED };
    } else if (failed) {
      data.googlePayNativeResultError = { status: DepositStatus.GOOGLE_PAY_FAILED };
    }
    form.submit();
  };

  const openGooglePayNative = () =>
    starsWebGooglePay &&
    starsWebGooglePay.requestPayment(getGooglePayNativeData(currency, amount, info, site));

  const googlePayButtonClicked = async (e: React.MouseEvent) => {
    e.preventDefault();
    const { valid } = form.getState();
    if (!amount || !valid) {
      form.submit();
      return;
    }
    setProcessing(true);
    openGooglePayNative();
    waitForEventWithTimeout().then((response) => handleResponse(response));
  };

  return (
    <CTAButton
      type="button"
      translation={TXTWCL_Deposit}
      disabled={processing}
      onClick={googlePayButtonClicked}
    />
  );
};

export default GooglePay;
